import { createHTTPError } from '@gitbook/util-debug';

import { RootParentSymbol } from '../revisions';
import { asPageWithDescendants, type Revision, RevisionPageType, RevisionType } from '../types';
import type { ComputedContentRevisionResult, RevisionComputations } from './types';
import { getComputeContentId, hash } from './utils';

/**
 * Apply a set of computations to a revision.
 */
export function applyRevisionComputations({
    revision,
    computations,
}: {
    revision: Revision;
    computations: RevisionComputations;
}): ComputedContentRevisionResult {
    const computedPageEntries = Object.entries(computations.pages);

    if (computedPageEntries.length === 0) {
        // No computed pages, return the current revision
        return { result: revision, complete: true, error: null };
    }

    //
    // Merge everything as a new revision
    // This revision is not saved in the store, as each layer has been cached.
    // We only ensure a unique ID but we don't need to care too much about its value over time.
    //
    const computedRevisionId = getComputeContentId(
        `${revision.id}_${hash(
            computedPageEntries.map(([pageId, entry]) => ({
                pageId,
                revisionId: entry.result?.id,
            }))
        )}`
    );
    const result: Revision = {
        ...structuredClone(revision),
        id: computedRevisionId,
        contentId: revision.contentId,
        type: RevisionType.Computed,
        parents: [revision.id],
    };

    let complete = true;
    let error: Error | null = null;

    computedPageEntries.forEach(([_computedPageId, entry]) => {
        const { computedPage, parentId, result: revisionLayer } = entry;
        const pages =
            parentId === RootParentSymbol
                ? result.rootPages
                : asPageWithDescendants(result.pages[parentId]).pages;

        // If the entry is not complete, we mark the result as not complete
        if (!entry.complete) {
            complete = false;
        }

        // Take the latest index as we could be processing multiple computed pages in the same parent
        const index = pages.indexOf(computedPage.id);
        if (index === -1) {
            throw new Error(`Couldn't find page ${computedPage.id} in parent`);
        }

        // Replace the computed page with the computed pages
        // We preserve the computed page in the `pages` map.
        pages.splice(index, 1, ...(revisionLayer ? revisionLayer.rootPages : []));
        delete result.pages[computedPage.id];

        if (revisionLayer) {
            Object.values(revisionLayer.pages).forEach((page) => {
                const existing = result.pages[page.id];
                if (existing) {
                    throw new createHTTPError.UnprocessableEntity(
                        `Computation by ${computedPage.computed.type} returned a page with an ID that already exists in the revision: ${page.id}`
                    );
                }

                result.pages[page.id] = page;
            });

            // Ensure no group pages is inserted outside the root
            if (parentId !== RootParentSymbol) {
                revisionLayer.rootPages.forEach((pageId) => {
                    const page = result.pages[pageId];

                    if (page.type === RevisionPageType.Group) {
                        // Convert the group to a document
                        result.pages[pageId] = {
                            ...page,
                            type: RevisionPageType.Document,
                        };
                    }
                });
            }

            // Merge the files
            Object.entries(revisionLayer.files).forEach(([fileId, file]) => {
                if (result.files[fileId]) {
                    throw new createHTTPError.UnprocessableEntity(
                        `Computation by ${computedPage.computed.type} returned a file with an ID that already exists in the revision: ${fileId}`
                    );
                }

                result.files[fileId] = file;
            });

            // Merge variables
            if (revisionLayer.variables) {
                result.variables ??= {};
                Object.assign(result.variables, revisionLayer.variables ?? {});
            }

            // Merge reusable contents
            Object.entries(revisionLayer.reusableContents ?? {}).forEach(([id, ruc]) => {
                if (result.reusableContents[id]) {
                    throw new createHTTPError.UnprocessableEntity(
                        `Computation by ${computedPage.computed.type} returned a reusable content with an ID that already exists in the revision: ${id}`
                    );
                }

                result.reusableContents[id] = ruc;
            });
        } else if (entry.error) {
            error = entry.error;
        }
    });

    return { result, complete, error };
}
